import type {
  TopologyLoopWireMatchResult,
} from '../../../../types/reference/sketch-entity-reference/TopologyLoopWireMatcher';
import type {
  TopologyWireRuntimeCandidate,
} from '../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
import type {
  TopologyBoundaryCurrentMember,
  TopologyBoundaryMemberCorrespondenceResult,
} from '../../../../types/reference/sketch-entity-reference/TopologyBoundaryMemberCorrespondence';
import type {
  SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import {
  TopologyBoundaryMemberCorrespondenceResolver,
} from './TopologyBoundaryMemberCorrespondenceResolver';

/**
 * topology-loop 历史成员到当前 Face inner Wire 的唯一匹配器。
 *
 * Wire 身份由完整成员序列共同决定。物理 ID 稳定时优先使用强身份；全部 ID 漂移时，
 * 继续使用语义、曲线类型和循环顺序选择候选，禁止因单个局部索引相同直接回绑。
 */
export class TopologyLoopWireMatcher {
  /** 历史成员与单条当前 Wire 的纯对应模块。 */
  private readonly correspondenceResolver =
    new TopologyBoundaryMemberCorrespondenceResolver();

  /**
   * 在当前 Face 的直接 InnerWire 中查找创建时选中的环。
   *
   * @param oldMembers 创建时的完整 loop 引用成员。
   * @param currentWires 当前 Face 的直接 InnerWire 集合。
   * @returns 唯一最佳候选、缺失或多义。
   */
  public match(
    oldMembers: readonly SketchEntityReferenceRecord[],
    currentWires: readonly TopologyWireRuntimeCandidate[],
  ): TopologyLoopWireMatchResult {
    if (currentWires.length === 0 || oldMembers.length === 0) {
      return { status: 'missing' };
    }

    const ranked = currentWires.map((wire) => {
      const correspondence = this.correspondenceResolver.resolve({
        previous: oldMembers,
        current: this.createCurrentMembers(wire),
      });
      return {
        wire,
        correspondence,
        rank: this.createEvidenceRank(correspondence),
      };
    }).filter((candidate) => {
      const persistentEvidence = candidate.rank[0] ?? 0;
      const semanticEvidence = candidate.rank[1] ?? 0;
      const subEntityEvidence = candidate.rank[2] ?? 0;
      return candidate.correspondence.matches.length > 0
        && persistentEvidence + semanticEvidence + subEntityEvidence > 0;
    });
    if (ranked.length === 0) {
      return { status: 'missing' };
    }

    /*
     * 不以 Worker Wire 枚举序号裁决同分候选。只有证据元组严格更强的 Wire 才能胜出，
     * 从而既允许唯一 InnerWire 在普通参数变化后恢复，也避免多个同形孔之间静默串绑。
     */
    ranked.sort((left, right) => this.compareRanks(right.rank, left.rank));
    const best = ranked[0]!;
    const equallyRanked = ranked.filter(candidate => (
      this.compareRanks(candidate.rank, best.rank) === 0
    ));
    return equallyRanked.length === 1
      ? {
          status: 'matched',
          wire: best.wire,
        }
      : { status: 'ambiguous' };
  }

  /**
   * 将当前 Wire 的来源序列转换为成员对应模块输入。
   */
  private createCurrentMembers(
    wire: TopologyWireRuntimeCandidate,
  ): TopologyBoundaryCurrentMember[] {
    return wire.edgeSources.map((source, currentIndex) => ({
      source,
      currentIndex,
      endpointAnchorRefs: wire.edgeCandidates[currentIndex]?.endpointAnchorRefs,
    }));
  }

  /**
   * 从实际一对一匹配中生成 Wire 候选证据元组。
   *
   * 元组优先级依次为：持久身份、语义来源、局部子实体身份、匹配覆盖和较少编辑。
   * `subEntityId` 位于语义之后，避免 topology index 漂移或复用压过真实来源族。
   */
  private createEvidenceRank(
    correspondence: TopologyBoundaryMemberCorrespondenceResult,
  ): number[] {
    let persistentMatchCount = 0;
    let semanticMatchCount = 0;
    let subEntityMatchCount = 0;
    correspondence.matches.forEach(({reference, current}) => {
      if (reference.sourceDescriptor.kind !== 'topology-edge') {
        return;
      }
      const previousLocator = reference.sourceDescriptor.locator;
      const currentLocator = current.source.locator;
      if (
        previousLocator.persistentRef
        && previousLocator.persistentRef === currentLocator.persistentRef
      ) {
        persistentMatchCount += 1;
      }
      if (
        previousLocator.semanticName
        && previousLocator.semanticName === currentLocator.semanticName
      ) {
        semanticMatchCount += 1;
      }
      if (
        previousLocator.subEntityId
        && previousLocator.subEntityId === currentLocator.subEntityId
      ) {
        subEntityMatchCount += 1;
      }
    });
    return [
      persistentMatchCount,
      semanticMatchCount,
      subEntityMatchCount,
      correspondence.matches.length,
      -(correspondence.inserted.length + correspondence.removed.length),
    ];
  }

  /**
   * 按证据优先级比较两个 Wire 元组。
   *
   * @returns left 更强返回正数，right 更强返回负数，同分返回零。
   */
  private compareRanks(left: readonly number[], right: readonly number[]): number {
    const length = Math.max(left.length, right.length);
    for (let index = 0; index < length; index += 1) {
      const difference = (left[index] ?? 0) - (right[index] ?? 0);
      if (difference !== 0) {
        return difference;
      }
    }
    return 0;
  }
}
